import React from 'react';
import { InputNumber, Segmented, Tooltip } from 'antd';
import { AddButton, isBlank, ItemActions, RichTextArea } from '../fields';
import { moveItem, removeAt, replaceAt } from '../listOps';

export type Speaker = 'her' | 'you';

export interface Bubble {
  from?: Speaker;
  text: string;
  delay?: number;
  [key: string]: unknown;
}

interface ChatBubbleProps {
  from: Speaker;
  avatar?: string;
  children: React.ReactNode;
  meta?: React.ReactNode;
}

/** One chat row laid out like the app: her messages on the left, yours on the right. */
export const ChatBubble: React.FC<ChatBubbleProps> = ({ from, avatar, children, meta }) => (
  <div className={`lbe-bubble-row from-${from}`}>
    <span className="lbe-avatar">{avatar ?? (from === 'you' ? 'You' : 'Her')}</span>
    <div className="lbe-bubble">
      {children}
      {meta && <div className="lbe-bubble-meta">{meta}</div>}
    </div>
  </div>
);

interface BubbleListProps {
  items: Bubble[];
  onChange: (items: Bubble[]) => void;
  /** `pick`: each message is from her or you. `her`: every message is hers and `from` isn't stored. */
  speakers: 'pick' | 'her';
  /**
   * `required`: every message has a delay (chat_simulator replies — ms after the user's pick).
   * `optional`: blank means automatic spacing (step_chat — ms from the start).
   */
  delay?: 'required' | 'optional';
  herAvatar?: string;
}

const SPEAKER_OPTIONS = [{ label: 'Her', value: 'her' }, { label: 'You', value: 'you' }];

export const BubbleList: React.FC<BubbleListProps> = ({ items, onChange, speakers, delay, herAvatar }) => {
  const update = (index: number, item: Bubble) => onChange(replaceAt(items, index, item));

  const setDelay = (index: number, value: number | null) => {
    const { delay: _old, ...rest } = items[index];
    update(index, value === null && delay === 'optional' ? rest as Bubble : { ...rest, delay: value ?? 0 } as Bubble);
  };

  const add = (from: Speaker) => {
    const bubble: Bubble = speakers === 'pick' ? { from, text: '' } : { text: '' };
    if (delay === 'required') {
      // Reply delays are absolute offsets, so keep them increasing.
      const last = items[items.length - 1]?.delay;
      bubble.delay = typeof last === 'number' ? last + 1500 : 1000;
    }
    onChange([...items, bubble]);
  };

  return (
    <>
      {items.map((item, i) => {
        const from: Speaker = speakers === 'her' || item.from !== 'you' ? 'her' : 'you';
        return (
          <ChatBubble
            key={i}
            from={from}
            avatar={from === 'her' ? herAvatar : undefined}
            meta={
              <>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {speakers === 'pick' && (
                    <Segmented
                      size="small"
                      value={from}
                      options={SPEAKER_OPTIONS}
                      onChange={(value) => update(i, { ...item, from: value as Speaker })}
                    />
                  )}
                  {delay && (
                    <Tooltip title={delay === 'required' ? 'Delay after the user picks this reply' : 'Delay from the start of the chat; empty = automatic'}>
                      <InputNumber<number>
                        size="small"
                        min={0}
                        step={100}
                        value={typeof item.delay === 'number' ? item.delay : null}
                        placeholder="auto"
                        suffix="ms"
                        style={{ width: 110 }}
                        onChange={(value) => setDelay(i, value)}
                      />
                    </Tooltip>
                  )}
                </span>
                <ItemActions
                  index={i}
                  count={items.length}
                  onMove={(to) => onChange(moveItem(items, i, to))}
                  onRemove={() => onChange(removeAt(items, i))}
                />
              </>
            }
          >
            <RichTextArea
              minRows={1}
              value={item.text}
              status={isBlank(item.text) ? 'warning' : undefined}
              placeholder={from === 'you' ? 'Your message' : 'Her message'}
              onChange={(text) => update(i, { ...item, text })}
            />
          </ChatBubble>
        );
      })}
      <div style={{ display: 'flex', gap: 8 }}>
        {speakers === 'pick' ? (
          <>
            <AddButton onClick={() => add('her')}>Her message</AddButton>
            <AddButton onClick={() => add('you')}>Your message</AddButton>
          </>
        ) : (
          <AddButton onClick={() => add('her')}>Her reply</AddButton>
        )}
      </div>
    </>
  );
};
